import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid } from 'lucide-react';
import { api, fmt, type System, type Row } from '../lib/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ExcelExportButton from '../components/ExcelExportButton';
import { cleanPolicyId, isHouseForSystem } from '../lib/format';
import type { SheetSpec } from '../lib/xlsx-export';

const ALL_AGENTS = '__all__';
const HOUSE_AGENT = '__house__';

/**
 * Policy Data view — paid rows only, scoped by pay run + agent, split by
 * carrier with cards on top. Unpaid rows live in the Pay Run workspace.
 */
export default function Pivot() {
  const { sys: sysSlug } = useParams<{ sys: string }>();
  const sys = sysSlug === 'messer' ? 'Messer' : sysSlug === 'empire-aca' ? 'Empire-ACA' : null;

  const runsQuery = useQuery({
    queryKey: ['pay-runs', sys],
    queryFn: () => api.listPayRuns(sys as System),
    enabled: !!sys,
  });

  const agentsQuery = useQuery({
    queryKey: ['agents-all'],
    queryFn: () => api.listAgents(),
  });

  // Sort runs: open first (if any), then closed by closed_at desc
  const runs = useMemo(() => {
    const all = runsQuery.data?.pay_runs ?? [];
    const open = all.filter((r) => r.status === 'open');
    const closed = all
      .filter((r) => r.status !== 'open')
      .sort((a, b) => (b.closed_at ?? '').localeCompare(a.closed_at ?? ''));
    return [...open, ...closed];
  }, [runsQuery.data]);

  // Default to most recent closed run with payments, fallback to open run, else first
  const defaultRunId =
    runs.find((r) => r.status !== 'open' && (r.payment_count ?? 0) > 0)?.pay_run_id ??
    runs.find((r) => r.status === 'open')?.pay_run_id ??
    runs[0]?.pay_run_id ??
    '';

  // URL-state hook so deep-links from the Pay Run workspace can pre-set
  // both filters. ?pay_run=<id> selects a run; ?agent=<id> selects an
  // agent (or 'house' for the house bucket).
  const [searchParams, setSearchParams] = useSearchParams();
  const [payRunId, setPayRunId] = useState<string>(searchParams.get('pay_run') ?? '');
  const [agentFilter, setAgentFilter] = useState<string>(
    searchParams.get('agent') ?? ALL_AGENTS,
  );
  const [activeCarrier, setActiveCarrier] = useState<string | null>(null);

  // Mirror state to URL so the page is bookmarkable / shareable
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (payRunId) next.set('pay_run', payRunId);
    else next.delete('pay_run');
    if (agentFilter !== ALL_AGENTS) next.set('agent', agentFilter);
    else next.delete('agent');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [payRunId, agentFilter, searchParams, setSearchParams]);

  const effectiveRunId = payRunId || defaultRunId;
  const effectiveRun = runs.find((r) => r.pay_run_id === effectiveRunId);
  const isOpenRun = effectiveRun?.status === 'open';

  // Open runs: rows aren't tagged with pay_run_id yet, so fetch the full
  // unpaid pool. Closed runs: fetch by pay_run_id.
  const rowsQuery = useQuery({
    queryKey: ['policy-data', sys, effectiveRunId, isOpenRun, agentFilter],
    queryFn: () =>
      api.rowsFiltered({
        sys: sys as System,
        pay_run_id: isOpenRun ? undefined : effectiveRunId,
        paid: isOpenRun ? false : undefined,
        agent_id: agentFilter !== ALL_AGENTS && agentFilter !== HOUSE_AGENT ? agentFilter : undefined,
      }),
    enabled: !!sys && !!effectiveRunId,
  });

  if (!sys) return <div className="alert alert-warning"><p>Pick a system.</p></div>;
  if (runsQuery.isLoading) return <LoadingSpinner label="Loading pay runs…" />;
  if (runs.length === 0) {
    return (
      <div className="space-y-4 animate-fadeIn">
        <Header sys={sys} />
        <div className="card text-center py-12">
          <p style={{ color: 'var(--color-text-muted)' }}>
            No pay runs yet. Drop a commission file in Imports to get started.
          </p>
        </div>
      </div>
    );
  }

  // Build agent dropdown — all agents in this system, plus a "House" entry
  // that resolves to all payout=false agent_ids
  const allAgents = (agentsQuery.data?.agents ?? []).filter(
    (a) =>
      a.agent_id !== 'unpaid' &&
      a.agent_id !== 'unknown' &&
      Array.isArray(a.system_access) &&
      a.system_access.includes(sys),
  );
  const houseIds = new Set(allAgents.filter((a) => isHouseForSystem(a, sys)).map((a) => a.agent_id));
  const payableAgents = allAgents
    .filter((a) => !houseIds.has(a.agent_id))
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  // If filter is "House", apply client-side (since House is a synthetic
  // id). Unmapped writing IDs (agent_id 'unknown'/'unpaid') roll into
  // House for reporting too — matches the backend pay-summary behavior.
  let rows = rowsQuery.data?.rows ?? [];
  if (agentFilter === HOUSE_AGENT) {
    rows = rows.filter(
      (r) =>
        houseIds.has(r.agent_id) || r.agent_id === 'unknown' || r.agent_id === 'unpaid',
    );
  }

  // Per-carrier breakdown for cards + tabs
  const byCarrier = new Map<string, { rows: Row[]; net: number }>();
  for (const r of rows) {
    const c = r.carrier || '?';
    if (!byCarrier.has(c)) byCarrier.set(c, { rows: [], net: 0 });
    const b = byCarrier.get(c)!;
    b.rows.push(r);
    b.net += r.net_amount || 0;
  }
  const carrierList = [...byCarrier.keys()].sort();
  const grandTotal = rows.reduce((s, r) => s + (r.net_amount || 0), 0);

  const currentCarrier =
    activeCarrier && byCarrier.has(activeCarrier) ? activeCarrier : carrierList[0] ?? null;
  const currentRows = currentCarrier ? byCarrier.get(currentCarrier)!.rows : [];

  const showAgentCol = agentFilter === ALL_AGENTS || agentFilter === HOUSE_AGENT;

  return (
    <div className="space-y-4 animate-fadeIn">
      <Header sys={sys} />

      {/* Filters */}
      <div className="card flex flex-wrap items-end gap-3">
        <div>
          <label className="label" style={{ marginBottom: 4 }}>
            Pay Run
          </label>
          <select
            className="select text-sm"
            value={effectiveRunId}
            onChange={(e) => setPayRunId(e.target.value)}
            style={{ minWidth: 240 }}
          >
            {runs.map((r) => (
              <option key={r.pay_run_id} value={r.pay_run_id}>
                {r.name}
                {r.status === 'open' ? ' (open)' : r.closed_at ? ` · closed ${r.closed_at.slice(0, 10)}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" style={{ marginBottom: 4 }}>
            Agent
          </label>
          <select
            className="select text-sm"
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            style={{ minWidth: 200 }}
          >
            <option value={ALL_AGENTS}>All agents</option>
            {houseIds.size > 0 && (
              <option value={HOUSE_AGENT}>House ({houseIds.size} agents)</option>
            )}
            <optgroup label="Payable">
              {payableAgents.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>
                  {a.display_name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {rows.length.toLocaleString()} rows · total{' '}
            <span className="font-mono" style={{ color: 'var(--color-text)' }}>
              {fmt.money(grandTotal)}
            </span>
          </div>
          <ExcelExportButton
            filename={`policy-data-${effectiveRunId || 'all'}-${agentFilter !== ALL_AGENTS ? agentFilter : 'all-agents'}.xlsx`}
            sheets={(): SheetSpec[] => {
              const headers = showAgentCol
                ? ['Policy', 'Policyholder', 'Agent', 'Raw', 'OVR', 'Agent net', 'Paid-to date']
                : ['Policy', 'Policyholder', 'Raw', 'OVR', 'Agent net', 'Paid-to date'];
              return carrierList.map((c) => {
                const b = byCarrier.get(c)!;
                const sortedRows = b.rows.slice().sort((a, z) => (z.net_amount || 0) - (a.net_amount || 0));
                const data: (string | number | null)[][] = [headers];
                for (const r of sortedRows) {
                  const base: (string | number | null)[] = [
                    cleanPolicyId(r.policy_id, r.writing_agent_id),
                    r.policyholder_name ?? '',
                  ];
                  if (showAgentCol) base.push(r.agent_name ?? '');
                  base.push(
                    r.raw_amount ?? null,
                    r.override_amount ?? null,
                    r.net_amount ?? null,
                    r.paid_to_date ?? r.transaction_date ?? '',
                  );
                  data.push(base);
                }
                // Totals row
                const totalLabel = `Total · ${b.rows.length} rows`;
                const totalRow: (string | number | null)[] = showAgentCol
                  ? [totalLabel, '', '']
                  : [totalLabel, ''];
                totalRow.push(
                  b.rows.reduce((s, r) => s + (r.raw_amount || 0), 0),
                  b.rows.reduce((s, r) => s + (r.override_amount || 0), 0),
                  b.rows.reduce((s, r) => s + (r.net_amount || 0), 0),
                  '',
                );
                data.push(totalRow);
                return { name: c, rows: data };
              });
            }}
          />
        </div>
      </div>

      {rowsQuery.isLoading ? (
        <LoadingSpinner label="Loading policy data…" />
      ) : rows.length === 0 ? (
        <div className="card text-center py-12">
          <p style={{ color: 'var(--color-text-muted)' }}>
            {isOpenRun
              ? 'No queued rows match this filter — drop a commission file in Imports to add some.'
              : 'No paid rows match this filter.'}
          </p>
        </div>
      ) : (
        <>
          {/* Cards: one per carrier with totals (always visible, summarize the filter) */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {carrierList.map((c) => {
              const b = byCarrier.get(c)!;
              return (
                <button
                  key={c}
                  onClick={() => setActiveCarrier(c)}
                  className="metric-card metric-card-primary text-left"
                  style={{
                    borderColor: currentCarrier === c ? 'var(--color-primary)' : undefined,
                    borderWidth: currentCarrier === c ? 2 : undefined,
                  }}
                >
                  <p
                    className="text-xs uppercase tracking-wider mb-1"
                    style={{ color: 'var(--color-text-muted)' }}
                  >
                    {c}
                  </p>
                  <p className="text-xl font-bold font-mono" style={{ color: 'var(--color-text)' }}>
                    {fmt.money(b.net)}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                    {b.rows.length} rows
                  </p>
                </button>
              );
            })}
          </div>

          {/* Detail rows for selected carrier (cards above already function as carrier tabs) */}
          <div className="card overflow-x-auto p-0">
            <table className="table" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ width: '18%' }}>Policy</th>
                  <th style={{ width: showAgentCol ? '22%' : '30%' }}>Policyholder</th>
                  {showAgentCol && <th style={{ width: '16%' }}>Agent</th>}
                  <th className="text-right" style={{ width: '11%' }}>Raw</th>
                  <th className="text-right" style={{ width: '11%' }}>OVR</th>
                  <th className="text-right" style={{ width: '11%' }}>Agent net</th>
                  <th style={{ width: '11%' }}>Paid-to date</th>
                </tr>
              </thead>
              <tbody>
                {currentRows
                  .slice()
                  .sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0))
                  .map((r) => (
                    <tr key={`${r.batch_id}-${r.row_idx}`}>
                      <td className="font-mono text-xs">{cleanPolicyId(r.policy_id, r.writing_agent_id)}</td>
                      <td>{r.policyholder_name}</td>
                      {showAgentCol && (
                        <td className="text-sm">
                          {r.agent_name}
                          {houseIds.has(r.agent_id) && (
                            <span
                              className="ml-2 text-xs"
                              style={{ color: 'var(--color-text-muted)' }}
                            >
                              (house)
                            </span>
                          )}
                        </td>
                      )}
                      <td className="text-right font-mono">{fmt.money(r.raw_amount)}</td>
                      <td
                        className="text-right font-mono text-xs"
                        style={{ color: 'var(--color-text-muted)' }}
                        title={r.override_formula || ''}
                      >
                        {fmt.money(r.override_amount)}
                      </td>
                      <td className="text-right font-mono font-semibold">
                        {fmt.money(r.net_amount)}
                      </td>
                      <td className="text-xs">
                        {fmt.dateOnly(r.paid_to_date ?? r.transaction_date)}
                      </td>
                    </tr>
                  ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: 'var(--color-table-header)', fontWeight: 600 }}>
                  <td colSpan={showAgentCol ? 3 : 2}>
                    Total · {currentRows.length} rows
                  </td>
                  <td className="text-right font-mono">
                    {fmt.money(currentRows.reduce((s, r) => s + (r.raw_amount || 0), 0))}
                  </td>
                  <td className="text-right font-mono">
                    {fmt.money(currentRows.reduce((s, r) => s + (r.override_amount || 0), 0))}
                  </td>
                  <td className="text-right font-mono">
                    {fmt.money(currentRows.reduce((s, r) => s + (r.net_amount || 0), 0))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Header({ sys }: { sys: System }) {
  return (
    <div>
      <h2 className="text-2xl font-semibold" style={{ color: 'var(--color-text)' }}>
        <LayoutGrid size={20} className="inline mr-2" style={{ color: 'var(--color-primary)' }} />
        Policy Data — {sys}
      </h2>
      <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
        Policies grouped by carrier. Closed runs show paid rows tagged to that run; open runs show
        the queued (unpaid) pool. Filter by pay run and agent. Cards are per-carrier totals; click
        a carrier card for per-policy detail.
      </p>
    </div>
  );
}
